import React from 'react';
import TodoListHeader from './components/TodoListHeader';
import TodoListContent from './components/TodoListContent';
import FokusOverlay from '../shared/FokusOverlay';
import {
  TODO_VIEW_MODES,
  SORT_OPTIONS,
  SWIMLANE_GROUP_OPTIONS,
} from './constants';
import {
  useTodoListData,
  useTodoListActions,
  useMissionModal,
  useFocusMission,
  useTodoClipboard,
} from './hooks';
import './styles/index.css';

// Re-export constants for external use
export { TODO_VIEW_MODES, SORT_OPTIONS, SWIMLANE_GROUP_OPTIONS };

/**
 * TodoListView - Main component for displaying tasks in list/tree/swimlane formats
 */
const TodoListView = ({
  tasks = [],
  statuses = [],
  categories = [],
  allTags = [],
  nestedSprints = [],
  allSprints = [],
  campaignId = '',
  campaignName = '',
  campaignIcon = null,
  campaignProgress = 0,
  breadcrumbPath = [],
  onNavigateToParent,
  onSelectSprint,
  onSprintIconChange,
  onRenameSprint,
  onStatusChange,
  onDeleteTask,
  onPasteTasks,
  onAddTask,
  onRenameTask,
  onOpenAttachments,
  onOpenNestedSprint,
  onOpenDetails,
  onPriorityChange,
  onDifficultyChange,
  onDueDateChange,
  onArchiveTask,
  onUnarchiveTask,
  onDependencyChange,
  onConditionsChange,
  onFocusTask,
  onAddSubtask,
  onIconChange,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onSetStatusAutoSync,
  showArchived = false,
  showActionableOnly = false,
  viewMode = TODO_VIEW_MODES.MASTER,
  onViewModeChange,
  sortBy = 'manual',
  onSortChange,
  filters = {},  // Shared filter object from KanbanFilterDropdown
  swimlaneGroupBy = 'status', // Legacy prop
  onSwimlaneGroupByChange,
  // New props for unified swimlane approach (same as Kanban)
  swimlaneSetting = 'none',
  showNestedItems = true,
  _todoModalTriggerRef,
  // Selection for Fokus mode
  selectedTaskIds = new Set(),
  onToggleSelect,
  // Fokus mode state
  isFokusMode = false,
  // Element visibility
  elementVisibility = {},
  // View navigation (for "Open in view" dropdown)
  currentView = 'todolist',
  customViews = [],
  activeCustomViewId = null,
  onViewChange,
  onSelectCustomView,
  onGoToTaskInView,
  // Decision handlers
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  // Empty state arrow indicator
  onShowAddMissionArrow,
  // For adding missions to nested sprints in Master view
  onAddMissionToSprint,
  // Custom fields
  customFields = [],
  onCustomFieldValueChange,
  // Deliverables warning modal
  onSubmitDeliverable,
  onNavigateToParentMission,
  localSprint = null,
  isLoadingNested = false,
}) => {

  // Compute derived data
  const {
    allTasksFlat,
    lockStates,
    viewData,
    completedCount,
    totalCount,
    remainingMinutes,
    remainingMinMinutes,
    missingEstCount,
    remainingBreakdown,
  } = useTodoListData({
    tasks,
    statuses,
    allTags,
    nestedSprints,
    allSprints,
    showArchived,
    showActionableOnly,
    viewMode,
    filters,
    sortBy,
    swimlaneGroupBy,
    // New props for unified swimlane approach
    swimlaneSetting,
    showNestedItems,
    // Custom fields for filtering
    customFields,
    localSprint,
  });

  // Action handlers
  const { handleToggleComplete, handleDrillIn } = useTodoListActions({
    allTasksFlat,
    statuses,
    onStatusChange,
    onOpenNestedSprint,
  });

  // Mission creation with auto-scroll
  const { handleAddMissionClick } = useMissionModal({ onAddTask, _todoModalTriggerRef });

  // Focus mission event listener (for search navigation)
  useFocusMission();

  // Scroll to task helper (for paste navigation)
  const scrollToTask = React.useCallback((taskId) => {
    const element = document.querySelector(`[data-todo-id="${taskId}"]`);
    if (element) {
      // Use instant scroll so element is immediately visible
      element.scrollIntoView({ behavior: 'instant', block: 'center' });
      
      // Flash highlight immediately after instant scroll
      // Duration matches the CSS animation (1.8s pulse + fade)
      element.classList.add('todo-item--highlighted');
      setTimeout(() => {
        element.classList.remove('todo-item--highlighted');
      }, 2000);
    }
  }, []);

  // Setter for selection
  const setSelectedTaskIds = React.useCallback((newSelection) => {
    // For TodoList, we need to toggle each task individually
    // Clear current selection and set new ones
    if (onToggleSelect) {
      // This is a simplified approach - in practice, the parent manages selection
      // The clipboard hook will call this with the new selection
    }
  }, [onToggleSelect]);

  // Sync DOM with React selection state (for external updates like focusMission, selectAll)
  // This ensures the DOM reflects the selection state when it changes
  React.useEffect(() => {
    // Clear all selected classes first
    document.querySelectorAll('.todo-item--selected').forEach(el => {
      el.classList.remove('todo-item--selected');
    });
    // Add selected class to currently selected items
    selectedTaskIds.forEach(taskId => {
      const todoEl = document.querySelector(`.todo-item[data-todo-id="${taskId}"]`);
      todoEl?.classList.add('todo-item--selected');
    });
  }, [selectedTaskIds]);

  // Clipboard hook for copy/paste/duplicate operations
  useTodoClipboard({
    tasks,
    selectedTaskIds,
    setSelectedTaskIds,
    nestedSprints,
    allSprints,
    allTags,
    customFields,
    onPasteTasks,
    onDeleteTask,
    scrollToTask,
  });

  // Build class name with fokus mode
  const viewClassName = `todolist-view${isFokusMode ? ' todolist-view--fokus-mode' : ''}`;

  return (
    <div className={viewClassName}>
      {/* FOKUS mode overlay - dithered blue vignette */}
      {/* skipEnterAnimation=true because if we're rendering, fokus was already active */}
      {isFokusMode && <FokusOverlay skipEnterAnimation />}
      
      <TodoListHeader
        campaignName={campaignName}
        campaignIcon={campaignIcon}
        campaignProgress={campaignProgress}
        breadcrumbPath={breadcrumbPath}
        completedCount={completedCount}
        totalCount={totalCount}
        remainingMinutes={remainingMinutes}
        remainingMinMinutes={remainingMinMinutes}
        missingEstCount={missingEstCount}
        remainingBreakdown={remainingBreakdown}
        onSelectSprint={onSelectSprint}
        onSprintIconChange={onSprintIconChange}
        onRenameSprint={onRenameSprint}
      />

      <div className="todolist-view__content">
        {isLoadingNested && (viewMode === TODO_VIEW_MODES.MASTER || showNestedItems) && (
          <div className="todolist-view__loading">
            <span className="todolist-view__loading-spinner" />
            <span className="todolist-view__loading-text">Loading nested campaigns...</span>
          </div>
        )}

        <TodoListContent
          viewData={viewData}
          tasks={tasks}
          statuses={statuses}
          allTags={allTags}
          lockStates={lockStates}
          showArchived={showArchived}
          handleToggleComplete={handleToggleComplete}
          handleDrillIn={handleDrillIn}
          handleAddMissionClick={handleAddMissionClick}
          onOpenDetails={onOpenDetails}
          onOpenAttachments={onOpenAttachments}
          onStatusChange={onStatusChange}
          onPriorityChange={onPriorityChange}
          onDifficultyChange={onDifficultyChange}
          onDueDateChange={onDueDateChange}
          onRenameTask={onRenameTask}
          onDeleteTask={onDeleteTask}
          onArchiveTask={onArchiveTask}
          onUnarchiveTask={onUnarchiveTask}
          onDependencyChange={onDependencyChange}
          onConditionsChange={onConditionsChange}
          onFocusTask={onFocusTask}
          onAddSubtask={onAddSubtask}
          onIconChange={onIconChange}
          onAddStatus={onAddStatus}
          onDeleteStatus={onDeleteStatus}
          onRenameStatus={onRenameStatus}
          onSetStatusAutoSync={onSetStatusAutoSync}
          selectedTaskIds={selectedTaskIds}
          onToggleSelect={onToggleSelect}
          elementVisibility={elementVisibility}
          currentView={currentView}
          customViews={customViews}
          activeCustomViewId={activeCustomViewId}
          currentCampaignId={campaignId}
          onViewChange={onViewChange}
          onSelectCustomView={onSelectCustomView}
          onGoToTaskInView={onGoToTaskInView}
          onDecisionAnswerChange={onDecisionAnswerChange}
          onDecisionOptionsChange={onDecisionOptionsChange}
          onShowAddMissionArrow={onShowAddMissionArrow}
          onAddMissionToSprint={onAddMissionToSprint}
          customFields={customFields}
          onCustomFieldValueChange={onCustomFieldValueChange}
          onSubmitDeliverable={onSubmitDeliverable}
          onNavigateToParentMission={onNavigateToParentMission}
          nestedSprints={nestedSprints}
          allSprints={allSprints}
        />
      </div>
    </div>
  );
};

export default TodoListView;
